import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Map as ImmutableMap } from 'immutable'
import { Box, Typography, Alert, TextField } from '@mui/material'
import MuiButton from 'gui/components/common/MuiButton'
import { MARKETING_PLAN_PROMPT } from 'services/prompts/prompts'
import { CollapsibleSection } from 'gui/components/common/CollapsibleSection'
import { useApi } from '../ai-chat/hooks/useApi'
import {
  useTranscriptFetcher,
  ProgressTracker,
  type ProgressStep,
  type OpenAIResponse,
  type AiToolsSharedTranscript,
} from './ai-helpers'

interface AIMarketingPlanGenerationProps {
  content: ImmutableMap<string, unknown>
  isViewOnlyMode: boolean
  featureMediaId?: number | string | null
  /** Same object as AITranscript / AIMetadataGeneration — uses edited draft for generation */
  sharedTranscript?: AiToolsSharedTranscript
}

type AIMarketingPlanGenerationCoreProps = Omit<AIMarketingPlanGenerationProps, 'sharedTranscript'> &
  AiToolsSharedTranscript

/**
 * Core: transcript bundle supplied by parent or local wrapper.
 */
function AIMarketingPlanGenerationCore ({
  content,
  isViewOnlyMode,
  featureMediaId,
  fetchTranscript,
  fetchedTranscript,
  transcriptLoading,
  transcriptError,
  transcriptDraft,
  onTranscriptDraftChange,
}: AIMarketingPlanGenerationCoreProps) {
  const { makeRequest } = useApi()
  /** True only when this section's Generate (empty draft) started the fetch — not AITranscript only */
  const pendingMarketingAfterTranscriptFetchRef = useRef(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [generatedPlan, setGeneratedPlan] = useState<string | null>(null)
  const [promptExpanded, setPromptExpanded] = useState<boolean>(false)
  const [transcriptExpanded, setTranscriptExpanded] = useState<boolean>(false)
  const [editablePrompt, setEditablePrompt] = useState<string>(MARKETING_PLAN_PROMPT)
  const [currentStep, setCurrentStep] = useState<ProgressStep | null>(null)
  const [completedSteps, setCompletedSteps] = useState<ProgressStep[]>([])

  const generateMarketingPlan = useCallback(
    async (transcriptText: string): Promise<void> => {
      try {
        const userPrompt = `${editablePrompt}\n\n${transcriptText}`

        const response = (await makeRequest('/openai/v1/chat/completions', {
          body: {
            model: 'gpt-4o',
            messages: [
              {
                role: 'system',
                content:
                  'You are a marketing expert for Gaia. Create comprehensive marketing plans based on video content. Return the marketing plan as formatted text/markdown.',
              },
              {
                role: 'user',
                content: userPrompt,
              },
            ],
            temperature: 0.7,
          },
        })) as unknown as OpenAIResponse

        const aiContent = response?.choices?.[0]?.message?.content

        if (!aiContent) {
          throw new Error('No content in OpenAI response')
        }

        setGeneratedPlan(aiContent)
        setCompletedSteps((prev) => [...prev, 'generating'])
        setCurrentStep('complete')
        setLoading(false)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error'
        setError(`Failed to generate marketing plan: ${errorMessage}`)
        setLoading(false)
        setCurrentStep(null)
      }
    },
    [editablePrompt, makeRequest],
  )

  useEffect(() => {
    if (!fetchedTranscript && !transcriptError && !transcriptLoading) return

    const processTranscript = async () => {
      if (transcriptError) {
        if (pendingMarketingAfterTranscriptFetchRef.current) {
          setError(transcriptError)
          setLoading(false)
          setCurrentStep(null)
          pendingMarketingAfterTranscriptFetchRef.current = false
        }
        return
      }

      if (fetchedTranscript) {
        if (!pendingMarketingAfterTranscriptFetchRef.current) {
          return
        }
        pendingMarketingAfterTranscriptFetchRef.current = false
        setCompletedSteps((prev) => [...prev, 'fetching'])
        setCurrentStep('generating')
        await generateMarketingPlan(fetchedTranscript)
      }
    }

    processTranscript()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedTranscript, transcriptError, transcriptLoading])

  const handleGeneratePlan = (): void => {
    const contentId = content.get('contentId') as number | string | undefined

    if (!contentId) {
      setError('Content ID not found')
      return
    }

    setError(null)
    setGeneratedPlan(null)
    setLoading(true)

    if (transcriptDraft.trim()) {
      pendingMarketingAfterTranscriptFetchRef.current = false
      setCompletedSteps(['fetching'])
      setCurrentStep('generating')
      generateMarketingPlan(transcriptDraft).catch(() => undefined)
      return
    }

    pendingMarketingAfterTranscriptFetchRef.current = true
    onTranscriptDraftChange('')
    setCurrentStep('fetching')
    setCompletedSteps([])
    fetchTranscript(contentId)
  }

  const handleRetry = (): void => {
    setError(null)
    handleGeneratePlan()
  }

  const handleCopyToClipboard = async (): Promise<void> => {
    if (!generatedPlan) return

    try {
      await navigator.clipboard.writeText(generatedPlan)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to copy to clipboard: ${errorMessage}`)
    }
  }

  const isButtonDisabled = !featureMediaId || isViewOnlyMode || loading

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h6" gutterBottom>
        AI Marketing Plan Generation
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Uses the same transcript as the Transcript section above (including any edits). You can
        adjust it again here before generating.
      </Typography>

      <CollapsibleSection
        title="Prompt"
        expanded={promptExpanded}
        onToggle={() => setPromptExpanded(!promptExpanded)}
      >
        <Box sx={{ p: 2, pt: 0 }}>
          <TextField
            fullWidth
            multiline
            rows={12}
            value={editablePrompt}
            onChange={(e) => setEditablePrompt(e.target.value)}
            disabled={loading || isViewOnlyMode}
            variant="outlined"
            sx={{
              backgroundColor: '#fff',
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.85rem',
              },
            }}
          />
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            <MuiButton
              size="small"
              variant="outlined"
              onClick={() => setEditablePrompt(MARKETING_PLAN_PROMPT)}
              disabled={loading || isViewOnlyMode}
            >
              Reset to Default
            </MuiButton>
          </Box>
        </Box>
      </CollapsibleSection>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <MuiButton color="primary" size="small" onClick={handleRetry}>
              Retry
            </MuiButton>
          }
        >
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <MuiButton
          variant="contained"
          color="primary"
          onClick={handleGeneratePlan}
          disabled={isButtonDisabled}
        >
          Generate Marketing Plan from AI
        </MuiButton>
      </Box>

      {(loading || currentStep) && (
        <ProgressTracker
          steps={[
            { key: 'fetching', label: 'Fetching Transcript' },
            {
              key: 'generating',
              label: 'Generating Marketing Plan',
              loadingLabel: 'Generating Marketing Plan (this takes a moment...)',
            },
          ]}
          currentStep={currentStep}
          completedSteps={completedSteps}
        />
      )}

      {(Boolean(fetchedTranscript) || transcriptDraft.length > 0) && (
        <CollapsibleSection
          title={`Transcript (${transcriptDraft.length.toLocaleString()} characters)`}
          expanded={transcriptExpanded}
          onToggle={() => setTranscriptExpanded(!transcriptExpanded)}
        >
          <Box sx={{ p: 2, pt: 0 }}>
            <TextField
              fullWidth
              multiline
              rows={12}
              value={transcriptDraft}
              onChange={(e) => onTranscriptDraftChange(e.target.value)}
              disabled={loading || isViewOnlyMode}
              variant="outlined"
              sx={{
                backgroundColor: '#fff',
                '& .MuiInputBase-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                },
              }}
            />
            <Box sx={{ mt: 1 }}>
              <MuiButton
                size="small"
                variant="outlined"
                onClick={() => onTranscriptDraftChange(fetchedTranscript ?? '')}
                disabled={loading || isViewOnlyMode}
              >
                Reset to original
              </MuiButton>
            </Box>
          </Box>
        </CollapsibleSection>
      )}

      {generatedPlan && (
        <Box
          sx={{
            mt: 3,
            p: 3,
            backgroundColor: '#f5f5f5',
            borderRadius: 2,
            border: '1px solid #e0e0e0',
          }}
        >
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6">Generated Marketing Plan</Typography>
            <MuiButton
              variant="outlined"
              size="small"
              onClick={handleCopyToClipboard}
              disabled={isViewOnlyMode}
            >
              Copy to Clipboard
            </MuiButton>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={20}
            value={generatedPlan}
            InputProps={{
              readOnly: true,
            }}
            variant="outlined"
            sx={{
              backgroundColor: '#fff',
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.9rem',
              },
            }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {generatedPlan.length.toLocaleString()} characters,{' '}
            {generatedPlan
              .split(/\s+/)
              .filter((word) => word.length > 0)
              .length.toLocaleString()}{' '}
            words
          </Typography>
        </Box>
      )}

      {!featureMediaId && !isViewOnlyMode && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Please attach media to this video before generating a marketing plan.
        </Alert>
      )}
    </Box>
  )
}

function AIMarketingPlanGenerationWithLocalTranscript (
  props: Omit<AIMarketingPlanGenerationProps, 'sharedTranscript'>,
) {
  const fetcher = useTranscriptFetcher()
  const [draft, setDraft] = useState('')

  useEffect(() => {
    if (fetcher.transcript != null && fetcher.transcript !== '') {
      setDraft(fetcher.transcript)
    }
  }, [fetcher.transcript])

  const transcriptSlot: AiToolsSharedTranscript = {
    fetchTranscript: fetcher.fetchTranscript,
    fetchedTranscript: fetcher.transcript,
    transcriptLoading: fetcher.loading,
    transcriptError: fetcher.error,
    transcriptDraft: draft,
    onTranscriptDraftChange: setDraft,
  }

  return <AIMarketingPlanGenerationCore {...props} {...transcriptSlot} />
}

function AIMarketingPlanGeneration (props: AIMarketingPlanGenerationProps) {
  const { sharedTranscript, ...rest } = props
  if (sharedTranscript) {
    return <AIMarketingPlanGenerationCore {...rest} {...sharedTranscript} />
  }
  return <AIMarketingPlanGenerationWithLocalTranscript {...rest} />
}

export default AIMarketingPlanGeneration
